import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';

export const headerBoxStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    justifyContent: 'space-between',
    gap: 2,
    mb: 3,
};

/** The two ways a patient joins the list, side by side and wrapping on a narrow screen. */
export const headerActionsStyles: SxProps<Theme> = {
    display: 'flex',
    gap: 1.5,
    flexWrap: 'wrap',
};

export const titleStyles: SxProps<Theme> = {
    fontWeight: 700,
    letterSpacing: '-0.015em',
};

export const subtitleStyles: SxProps<Theme> = {
    color: 'text.secondary',
};

/**
 * The filters. They stack on a phone, where a search field squeezed next to a dropdown
 * leaves neither wide enough to read what was typed into it.
 */
export const filterBarStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'stretch', sm: 'center' },
    gap: 2,
    mb: 3,
};

export const searchFieldStyles: SxProps<Theme> = {
    flex: 1,
    minWidth: { sm: 260 },
};

export const statusFieldStyles: SxProps<Theme> = {
    minWidth: { sm: 180 },
};

/** Sits with the filters, so "3 of 40" reads as the answer to what was just typed. */
export const resultCountStyles: SxProps<Theme> = {
    color: 'text.secondary',
    whiteSpace: 'nowrap',
};

export const alertStyles: SxProps<Theme> = {
    mb: 3,
    borderRadius: 3,
};

export const loadingBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    py: 8,
};

export const emptyStateBoxStyles: SxProps<Theme> = {
    textAlign: 'center',
    py: 8,
};

export const emptyStateIconStyles = (theme: Theme): SxProps<Theme> => ({
    fontSize: 64,
    color: theme.palette.grey[300],
    mb: 2,
});

/** Paper gives the table its own surface, and the container scrolls rather than the page. */
export const tableContainerStyles: SxProps<Theme> = {
    borderRadius: 3,
    border: '1px solid rgba(16,24,40,0.06)',
};

export const headerCellStyles: SxProps<Theme> = {
    fontWeight: 700,
    whiteSpace: 'nowrap',
};

// Rows used to be dimmed when the person had no live access. They are not any more: the
// status chip already says so, and opacity said it a second time in a way that also
// faded the phone number and email -- exactly the details a therapist needs to reach
// somebody who has not answered their invitation yet. Not having claimed an account is
// the normal state of a patient the day they are evaluated, not a degraded one.

/** An email address is an identifier, so it reads better in a mono face. */
export const identifierCellStyles: SxProps<Theme> = {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    color: 'text.secondary',
    maxWidth: 260,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

/** Same mono face as the email, but never wrapped -- a broken number is unusable. */
export const phoneCellStyles: SxProps<Theme> = {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
};

/** The dash standing in for a number nobody has given. */
export const phoneMissingStyles: SxProps<Theme> = {
    color: 'text.disabled',
};

/**
 * Capped so a patient with several therapists wraps onto a second line instead of
 * pushing the status and the action off the side of a narrow screen.
 */
export const therapistsCellStyles: SxProps<Theme> = {
    maxWidth: 260,
};

/**
 * The row's actions. Right-aligned and allowed to wrap: a row carries one button or two
 * depending on whether there is an invitation outstanding, and on a narrow screen the
 * second one drops below the first rather than squeezing both.
 */
export const rowActionsBoxStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 1,
};

/**
 * The way to the next page, centred under whichever of the two shapes is on screen. The
 * same recipe as the evaluations list, and deliberately identical: two lists that page
 * should not page differently.
 */
export const showMoreBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    mt: 2,
};
